/**
 * Detalle de un pedido del historial (ADM-09), en el modal del backoffice.
 *
 * El id viene de la URL (`?pedido=`), asi que el modal se puede enlazar y
 * sobrevive a recargar. Se pide el pedido completo al abrir y no se reusa la
 * fila de la tabla: el detalle trae lo que la tabla no necesita (pago,
 * correos, fotos de lo comprado).
 *
 * Subir la boleta o factura vuelve a pedir el pedido (`version`), para que el
 * detalle muestre el folio nuevo y el correo que salio con el.
 */
import { useState } from 'react';

import type { Pedido, PedidoAdminDetalle } from '@bandu/compartido';

import { usePeticion } from '../../hooks/use-peticion';
import { obtenerPedidoAdmin } from '../../servicios/administracion.servicio';
import { fechaHoraCompleta } from '../../utilidades/formato';
import { AvisoError, Cargando } from '../comunes/EstadoPeticion';
import { DetallePedido } from './DetallePedido';
import { Modal } from './Modal';

export interface ModalPedidoProps {
  /** Nulo mantiene el modal cerrado. */
  id: number | null;
  /** La ruta desde donde se abrio, para que "Volver" de una ficha de producto regrese aca. */
  volver: string;
  onCerrar(): void;
  /** Para que la tabla de atras pinte el documento nuevo sin volver a pedirse. */
  onDocumentoSubido(pedido: Pedido): void;
}

function sinPedido(): Promise<PedidoAdminDetalle | null> {
  return Promise.resolve(null);
}

export function ModalPedido({ id, volver, onCerrar, onDocumentoSubido }: ModalPedidoProps) {
  const [version, setVersion] = useState(0);

  const { estado, datos, error } = usePeticion(
    (senal) => (id === null ? sinPedido() : obtenerPedidoAdmin(id, senal)),
    [id ?? 0, version],
  );

  return (
    <Modal
      titulo={datos !== null ? `Pedido ${datos.referencia}` : 'Pedido'}
      bajada={datos !== null ? `Creado el ${fechaHoraCompleta(datos.creadoEn)}` : undefined}
      abierto={id !== null}
      ancho="extra"
      onCerrar={onCerrar}
    >
      {estado === 'cargando' ? <Cargando que="el pedido" /> : null}
      {estado === 'error' ? <AvisoError error={error} /> : null}
      {datos !== null ? (
        <DetallePedido
          pedido={datos}
          volver={volver}
          onDocumentoSubido={(pedido) => {
            onDocumentoSubido(pedido);
            setVersion((valor) => valor + 1);
          }}
        />
      ) : null}
    </Modal>
  );
}
